import React from 'react';
import PropTypes from 'prop-types';
import path from 'path';
import { observer } from 'mobx-react';
import { observable, action, computed } from 'mobx';

import { Center } from 'legacyComponents/styled/lib';
import { FakeLink } from 'legacyComponents/styles';
import { PreviewLoader } from 'apps/viewer/views/previewLoader';

import {
  getFormatObject,
  getPreviewer,
  viewerURLtoPreviewer,
  viewerURLtoFilename,
  viewerURLToParams,
} from 'libs/rendering/utils';

import {
  ButtonStyled,
  PreviewCard,
  FallbackText,
  WIPpreviewer,
} from './styles';

class ViewerStore {
  @observable name;
  @observable previewURL;
  @observable extension;
  @observable previewType;

  constructor(props) {
    this.name = viewerURLtoFilename(props.viewerURL);
    this.previewURL = viewerURLtoPreviewer(props.viewerURL);
    this.extension = path.extname(this.name).replace('.', '');

    // - this code runs for previewers embedded into readme's, so
    // we don't want to query the files here (not sure if we really
    // don't query them, but there is no actual file here and there
    // shouldn't be imho (I checked, we do query them))
    // - we need formatObject which tells us which viewer to use, and
    // getFormatObject uses mimetype OR filename to determine what viewer
    // needs to be loaded, so we can pass in a dummy file with filename
    // (which we extracted from the url) and get the correct formatObject
    const { formatObject } = getFormatObject({
      filename: this.name,
      mimetype: '',
    });
    this.previewType = getPreviewer(formatObject);
  }

  @observable viewerEnabled = false;
  @observable error = null;
  @observable width = null;

  @action setViewerEnabled = enabled => {
    this.viewerEnabled = enabled;
  };

  @action setWidth = width => {
    this.width = width;
  };

  @action setError = error => {
    this.error = error;
  };

  @action errorEvent = event => {
    const errorElement = event.target.error || event.currentTarget.error;
    this.setError(
      errorElement
        ? errorElement.message
        : 'The preview for this file is currently being generated.',
    );
  };

  @computed get height() {
    if (!this.error && ['image', 'video', 'pdf'].includes(this.previewType)) {
      return 'auto';
    }
    if (!this.width) return '400';
    return (this.width * 3) / 4;
  }
}

@observer
export class FilePreviewer extends React.Component {
  constructor(props) {
    super(props);
    this.store = new ViewerStore(this.props);
  }

  onHoverEnter = e => {
    const video = e.currentTarget.querySelector('video');
    if (video) {
      video.play();
    }
  };

  onHoverLeave = e => {
    const video = e.currentTarget.querySelector('video');
    if (video) {
      video.pause();
    }
  };

  viewFile = () => {
    this.store.setError(null);
    this.store.setViewerEnabled(true);
  };

  componentDidMount() {
    if (!this.ref) return;
    const rect = this.ref.getBoundingClientRect();
    this.store.setWidth(rect.width);
  }

  render() {
    const {
      viewerURL,
      children,
      allowViewer = false,
      isFinderPreview,
      previewMode,
    } = this.props;
    const {
      previewType,
      extension,
      previewURL,
      viewerEnabled,
      error,
    } = this.store;

    let outputHeight =
      !isFinderPreview && previewType === 'text' ? null : this.store.height;
    if (previewMode === 'chat') {
      outputHeight = 'auto';
    }

    const typeParams = viewerURLToParams(viewerURL);

    let previewer = null;
    switch (previewType) {
      case 'image': {
        previewer = <img className='preview-element' src={previewURL} />;
        break;
      }
      case 'video': {
        previewer = <PreviewLoader {...typeParams.params} />;
        break;
      }
      case 'looping-video': {
        if (viewerEnabled) {
          previewer = <PreviewLoader {...typeParams.params} />;
          break;
        }
        if (error) {
          previewer = (
            <Center inherit>
              <FallbackText>{error}</FallbackText>
            </Center>
          );
          break;
        }
        previewer = (
          <Center inherit>
            <video
              onError={this.store.errorEvent}
              className='preview-element'
              loop
              muted
            >
              <source src={previewURL} />
              <FallbackText>
                Your browser doesn&apos;t support HTML video
              </FallbackText>
            </video>
          </Center>
        );
        break;
      }
      case 'pdf': {
        if (error) {
          previewer = (
            <Center inherit>
              <FallbackText>{error}</FallbackText>
            </Center>
          );
          break;
        }
        if (viewerEnabled) {
          previewer = <PreviewLoader {...typeParams.params} />;
          break;
        }
        previewer = (
          <img
            onError={this.store.errorEvent}
            className='preview-element'
            src={previewURL}
          />
        );
        break;
      }
      case 'text': {
        previewer = <PreviewLoader {...typeParams.params} lineWrapping />;
        break;
      }
      case 'wip': {
        if (viewerEnabled) {
          previewer = (
            <WIPpreviewer>
              <PreviewLoader {...typeParams.params} />
            </WIPpreviewer>
          );
          break;
        }
        const loadEmbedded = allowViewer ? (
          <p>
            Click <FakeLink onClick={this.viewFile}>here</FakeLink> to load the
            embeded file.
          </p>
        ) : null;
        previewer = (
          <Center inherit>
            <FallbackText>
              There&apos;s no thumbnail available for {extension.toUpperCase()}{' '}
              files yet. {loadEmbedded}
            </FallbackText>
          </Center>
        );
        break;
      }
      default: {
        break;
      }
    }

    const overlay = allowViewer &&
      !error &&
      ['looping-video', 'pdf'].includes(previewType) &&
      !viewerEnabled && (
        <div className='overlay'>
          <ButtonStyled
            ariaLabel='Play'
            onClick={this.viewFile}
            text={previewType === 'looping-video' ? 'Load Model' : 'Load PDF'}
            iconViewBox='0 0 40 40'
            icon='plane'
          />
        </div>
      );

    const content = previewer ? (
      <>
        {previewer}
        {overlay}
      </>
    ) : null;

    return (
      <PreviewCard
        ref={ref => {
          this.ref = ref;
        }}
        editorMode={this.props.editorMode}
        outputHeight={outputHeight}
        onMouseOver={
          previewType === 'looping-video' ? this.onHoverEnter : undefined
        }
        onMouseOut={
          previewType === 'looping-video' ? this.onHoverLeave : undefined
        }
      >
        {children}
        {previewMode && previewMode === 'chat' ? null : content}
      </PreviewCard>
    );
  }
}

FilePreviewer.propTypes = {
  children: PropTypes.node,
  viewerURL: PropTypes.string,
  allowViewer: PropTypes.bool,
  isFinderPreview: PropTypes.bool,
  editorMode: PropTypes.bool,
};
